import PopUp from "../../../components/containers/PopUp";
import { useSelector } from "react-redux";
import React, { useState, useEffect, useRef } from "react";
import { getBySlug } from "../../../server/Category";
import { normalizeCategoryProduct } from "../../../utils/categoryPeerImages";
import "./FamilyChangeDialog.scss";
import ReactDOM from "react-dom";

const productIdOf = (item) =>
  String(item?.productId ?? item?.product_id ?? item?.id ?? "");

const idsFromMarked = (markedProducts) =>
  new Set((markedProducts || []).map(productIdOf).filter(Boolean));

const FamilyChangeDialog = ({
  product,
  close,
  onConfirm,
  markedProducts,
  massage,
  slug,
}) => {
  const stationId = useSelector((state) => state.user.stationId);

  const [familyProducts, setFamilyProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(() =>
    idsFromMarked(markedProducts),
  );
  const [familyName, setFamilyName] = useState("");
  const [isConfirming, setIsConfirming] = useState(false);
  const selectAllIntentRef = useRef(false);

  useEffect(() => {
    if (!stationId || !slug) return;

    let cancelled = false;

    getBySlug(stationId, slug, false, true).then((res) => {
      if (cancelled || !res) return;
      const products = (Array.isArray(res.products) ? res.products : [])
        .map(normalizeCategoryProduct)
        .filter(Boolean);
      setFamilyProducts(products);
      setFamilyName(res.name || "");
    });

    return () => {
      cancelled = true;
    };
  }, [stationId, slug]);

  useEffect(() => {
    if (!selectAllIntentRef.current || !familyProducts.length) return;
    setSelectedProducts(
      new Set(familyProducts.map((prod) => productIdOf(prod))),
    );
  }, [familyProducts]);

  const allSelected =
    familyProducts.length > 0 &&
    familyProducts.every((prod) => selectedProducts.has(productIdOf(prod)));

  const handleProductToggle = (productId) => {
    if (isConfirming) return;
    const id = productIdOf({ productId });
    selectAllIntentRef.current = false;
    const next = new Set(selectedProducts);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedProducts(next);
  };

  const handleSelectAllToggle = () => {
    if (isConfirming) return;
    if (allSelected) {
      selectAllIntentRef.current = false;
      setSelectedProducts(new Set());
      return;
    }
    selectAllIntentRef.current = true;
    setSelectedProducts(
      new Set(familyProducts.map((prod) => productIdOf(prod))),
    );
  };

  const handleConfirm = async () => {
    if (selectedProducts.size === 0 || isConfirming) {
      if (!isConfirming && selectedProducts.size === 0) {
        alert("אנא בחר לפחות מוצר אחד לשינוי");
      }
      return;
    }

    const chosen = familyProducts.filter((prod) =>
      selectedProducts.has(productIdOf(prod)),
    );
    if (!chosen.length) {
      alert("אנא בחר לפחות מוצר אחד לשינוי");
      return;
    }

    setIsConfirming(true);
    try {
      await onConfirm(chosen);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleClose = () => close();

  return ReactDOM.createPortal(
    <PopUp width="700px" close={handleClose}>
      <div className="family-change-dialog">
        <div className="dialog-header">
          <h3>שינוי מוצרים במשפחה</h3>
          <p>
            מוצר זה שייך למשפחת "
            <span className="family-category-name">{familyName}</span>
            ".
          </p>
          <p>{massage}</p>
        </div>

        <div className="products-container">
          <div className="select-all-container">
            <label className="product-item select-all">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAllToggle}
                className="product-checkbox"
                disabled={isConfirming}
              />
              <span className="product-details">
                <strong>בחר הכל ({familyProducts.length} מוצרים)</strong>
              </span>
            </label>
          </div>
          <div className="products-list">
            {familyProducts.map((prod) => {
              const id = productIdOf(prod);
              return (
                <label key={id} className="product-item">
                  <input
                    type="checkbox"
                    checked={selectedProducts.has(id)}
                    onChange={() => handleProductToggle(id)}
                    className="product-checkbox"
                    disabled={isConfirming}
                  />
                  <span className="product-details">
                    <span className="product-name">{prod.name}</span>
                    {prod.pricing && prod.pricing.price && (
                      <span className="product-price">
                        ₪{prod.pricing.price}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="dialog-actions">
          <button
            className="ui-btn-brand confirm-button"
            onClick={handleConfirm}
            disabled={selectedProducts.size === 0 || isConfirming}
          >
            {isConfirming
              ? "מעדכן..."
              : `אישור (${selectedProducts.size} מוצרים נבחרו)`}
          </button>
          <button
            className="cancel-button"
            onClick={handleClose}
            disabled={isConfirming}
          >
            ביטול
          </button>
        </div>
      </div>
    </PopUp>,
    document.body,
  );
};

export default FamilyChangeDialog;
